import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import apiClient from "utils/apiClient";

const AVATAR_COLORS = ["#5c6bc0", "#10b981", "#f59e0b", "#ec4899", "#0ea5e9", "#8b5cf6", "#ef4444"];
const avatarColor = (id) => AVATAR_COLORS[id % AVATAR_COLORS.length];

const SUBSCRIPTIONS = ["free", "pro", "premium"];

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ─── small building blocks ────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "#9ca3af",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        mb: 1.5,
        mt: 3,
      }}
    >
      {children}
    </Typography>
  );
}
SectionTitle.propTypes = { children: PropTypes.node.isRequired };

function KV({ label, value }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" sx={{ color: "#9ca3af", fontWeight: 600, display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "#111827", fontWeight: 500, mt: 0.25 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}
KV.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
KV.defaultProps = { value: null };

function StatBox({ label, value, accent }) {
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#f9fafb",
        border: "1px solid #f0f0f0",
        borderRadius: "12px",
        p: 1.5,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h5"
        fontWeight={800}
        sx={{ color: accent || "#111827", lineHeight: 1, mb: 0.25 }}
      >
        {value ?? "—"}
      </Typography>
      <Typography variant="caption" sx={{ color: "#9ca3af", fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}
StatBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  accent: PropTypes.string,
};
StatBox.defaultProps = { value: null, accent: null };

// ─── tabs ─────────────────────────────────────────────────────────────────────

function OverviewTab({ data, onRoleChange, onSubscriptionChange }) {
  return (
    <Box>
      <SectionTitle>Identity</SectionTitle>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0} sx={{ rowGap: 0 }}>
        <KV label="Username" value={data.username} />
        <KV label="Email" value={data.email} />
        <KV label="Joined" value={fmt(data.created_at)} />
        <KV label="Last login" value={fmt(data.last_login)} />
      </Box>

      <SectionTitle>Role & Subscription</SectionTitle>
      <Box display="flex" gap={2}>
        <Box flex={1}>
          <Typography
            variant="caption"
            sx={{ color: "#9ca3af", fontWeight: 600, display: "block", mb: 0.75 }}
          >
            Role
          </Typography>
          <Select
            value={data.role}
            size="small"
            fullWidth
            onChange={(e) => onRoleChange(e.target.value)}
            sx={{ borderRadius: "10px", fontSize: "0.83rem" }}
          >
            <MenuItem value="user">user</MenuItem>
            <MenuItem value="admin">admin</MenuItem>
          </Select>
        </Box>
        <Box flex={1}>
          <Typography
            variant="caption"
            sx={{ color: "#9ca3af", fontWeight: 600, display: "block", mb: 0.75 }}
          >
            Subscription
          </Typography>
          <Select
            value={data.subscription || "free"}
            size="small"
            fullWidth
            onChange={(e) => onSubscriptionChange(e.target.value)}
            sx={{ borderRadius: "10px", fontSize: "0.83rem" }}
          >
            {SUBSCRIPTIONS.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "0.83rem" }}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <SectionTitle>Gamification</SectionTitle>
      <Box display="flex" gap={1} mb={1}>
        <StatBox label="Level" value={data.stats?.level} accent="#5c6bc0" />
        <StatBox label="XP" value={data.stats?.points} accent="#8b5cf6" />
        <StatBox
          label="Streak"
          value={data.stats?.streak !== undefined ? `${data.stats.streak}d` : null}
          accent="#f59e0b"
        />
      </Box>
      <Box display="flex" gap={1}>
        <StatBox label="Recipes cooked" value={data.stats?.total_recipes_cooked} />
        <StatBox label="Schedules done" value={data.stats?.total_schedules_completed} />
        <StatBox label="Distinction" value={data.stats?.distinction} />
      </Box>
    </Box>
  );
}

OverviewTab.propTypes = {
  data: PropTypes.object.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  onSubscriptionChange: PropTypes.func.isRequired,
};

function ActivityTab({ completions }) {
  if (!completions?.length)
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        No activity yet.
      </Typography>
    );
  return (
    <Box>
      <SectionTitle>Last {completions.length} recipes cooked</SectionTitle>
      {completions.map((c, i) => (
        <Box
          key={i}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 1.25, borderBottom: "1px solid #f7f7f7" }}
        >
          <Typography variant="body2" sx={{ color: "#111827", fontWeight: 500 }}>
            {c.recipe_title || `Recipe #${c.recipe_id}`}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9ca3af", flexShrink: 0, ml: 2 }}>
            {fmt(c.completed_at)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

ActivityTab.propTypes = { completions: PropTypes.array.isRequired };

// ─── main drawer ──────────────────────────────────────────────────────────────

function AdminUserDetail({ userId, onClose, onDeleted }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [newPassword, setNewPassword] = useState("");
  const [pwMsg, setPwMsg] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setData(null);
    setError(null);
    setTab(0);
    setNewPassword("");
    setPwMsg(null);
    apiClient
      .get(`/api/admin/users/${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Failed to load user"));
  }, [userId]);

  const handleRoleChange = (newRole) => {
    apiClient
      .patch(`/api/admin/users/${userId}`, { role: newRole })
      .then((r) => setData((d) => ({ ...d, role: r.data.role })))
      .catch(() => alert("Failed to update role"));
  };

  const handleSubscriptionChange = (newSub) => {
    setData((d) => ({ ...d, subscription: newSub }));
    // placeholder — wire up when subscription model exists
  };

  const handlePasswordReset = () => {
    if (!newPassword) return;
    apiClient
      .post(`/api/admin/users/${userId}/reset-password`, { new_password: newPassword })
      .then(() => {
        setPwMsg("Password updated.");
        setNewPassword("");
      })
      .catch((e) => setPwMsg(e?.response?.data?.detail || "Failed to update password"));
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete user "${data?.username}"? This cannot be undone.`)) return;
    apiClient
      .delete(`/api/admin/users/${userId}`)
      .then(() => {
        onClose();
        if (onDeleted) onDeleted(userId);
      })
      .catch(() => alert("Failed to delete user"));
  };

  return (
    <Drawer
      anchor="right"
      open={!!userId}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 520 },
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Fixed header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" gap={2}>
            {data && (
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: avatarColor(data.id),
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {data.username[0].toUpperCase()}
              </Avatar>
            )}
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {data?.username || "Loading…"}
              </Typography>
              {data && (
                <Box display="flex" gap={0.75} mt={0.5} flexWrap="wrap">
                  <Chip
                    label={data.role}
                    size="small"
                    sx={{
                      bgcolor: data.role === "admin" ? "#fee2e2" : "#eef0fb",
                      color: data.role === "admin" ? "#ef4444" : "#5c6bc0",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      height: 20,
                    }}
                  />
                  <Chip
                    label={data.subscription || "free"}
                    size="small"
                    sx={{
                      bgcolor:
                        data.subscription === "pro" || data.subscription === "premium"
                          ? "#fef3c7"
                          : "#f3f4f6",
                      color:
                        data.subscription === "pro" || data.subscription === "premium"
                          ? "#d97706"
                          : "#6b7280",
                      fontWeight: 600,
                      fontSize: "0.65rem",
                      height: 20,
                    }}
                  />
                  {data.stats?.distinction && (
                    <Chip
                      label={data.stats.distinction}
                      size="small"
                      sx={{ bgcolor: "#f3f4f6", color: "#6b7280", fontSize: "0.65rem", height: 20 }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af", mt: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {data && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mt: 2,
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                textTransform: "none",
                fontSize: "0.82rem",
                fontWeight: 500,
                py: 0,
              },
              "& .MuiTabs-indicator": { height: 2 },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Activity" />
            <Tab label="Actions" />
          </Tabs>
        )}
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 4 }}>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 3 }}>
            {error}
          </Typography>
        )}
        {!data && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Loading…
          </Typography>
        )}

        {data && tab === 0 && (
          <OverviewTab
            data={data}
            onRoleChange={handleRoleChange}
            onSubscriptionChange={handleSubscriptionChange}
          />
        )}
        {data && tab === 1 && <ActivityTab completions={data.recent_completions || []} />}

        {data && tab === 2 && (
          <Box>
            <SectionTitle>Change Password</SectionTitle>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
              Set a new password for this user. They will need to use it on next login.
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="New password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPwMsg(null);
              }}
              sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            {pwMsg && (
              <Typography
                variant="caption"
                sx={{
                  color: pwMsg.includes("updated") ? "#10b981" : "#ef4444",
                  display: "block",
                  mb: 1,
                }}
              >
                {pwMsg}
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={<LockResetIcon />}
              onClick={handlePasswordReset}
              disabled={!newPassword}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                bgcolor: "#5c6bc0",
                boxShadow: "none",
                "&:hover": { bgcolor: "#4a5ab0", boxShadow: "none" },
              }}
            >
              Set password
            </Button>

            <Divider sx={{ my: 3 }} />

            <SectionTitle>Danger Zone</SectionTitle>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
              Permanently delete this account and all associated data.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDelete}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Delete account
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

AdminUserDetail.propTypes = {
  userId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
};

AdminUserDetail.defaultProps = {
  userId: null,
  onDeleted: null,
};

export default AdminUserDetail;
